/**
 * artifactRegistry.js — THE declarative source of truth for Metric Studio.
 *
 * WHAT: Defines the EIGHT artifact types Studio authors (wizard, metric, flag,
 *   worklist, insight, attribute, quality, engagement) and everything that varies between
 *   them: REST urls, list columns, and the ordered detail SECTIONS. The generic
 *   shell (ArtifactList + ArtifactDetail) reads these entries and renders both the
 *   list and the detail tabs — so adding/altering an artifact is a config edit
 *   here, NOT new layout code. Also exports SECTION_ICON, ARTIFACT_ORDER,
 *   GROUP_ORDER, artifactsByGroup(), and getArtifact(key).
 *
 * WHERE: Imported by MetricStudio.vue (tab bar, create payloads, artifact lookup),
 *   ArtifactList.vue (columns/searchFields), and ArtifactDetail.vue (sections +
 *   SECTION_ICON). Not a component — plain JS module.
 *
 * CONTRACT — an ARTIFACTS[key] entry:
 *   key/label/singular — identity + display strings.
 *   group ('Performance'|'Setup') + icon + accent (hex) — grouping/theming.
 *   listUrl (string) / detailUrl(id) / deleteUrl(id)? — REST resources; deleteUrl
 *     falls back to detailUrl when absent. idField — the row's id key.
 *   deletePreviewUrl(id)? — GET returning {can_delete, blockers[], dependents[]}.
 *     Present => the × opens the itemised DeleteMetricModal (why it's blocked, or
 *     what else the delete takes with it). Absent => the shared yes/no modal.
 *   rowFilter(row)? — scopes a shared listUrl to a subset. `metric` and `quality`
 *     both read composable-metrics/ and split on measure_code, so a quality
 *     measure appears in exactly one tab.
 *   managerTab? / managerTabs? — { key, label, component } (or a list of them):
 *     extra sub-tabs beside the list, each rendering a full manager. Quality has
 *     two: Contract Assignments and Contract Scoring.
 *   searchFields[] — fields the list search box scans.
 *   columns[] — list column defs: { key, label, width, grow, mono, muted, date,
 *     check, dot, chip, badge, count, compute(row) } — drive ArtifactList
 *     rendering/sort. `count` renders a reference tally: non-zero bold, zero faint.
 *   rowMuted(row)? — dim the whole row (and drop its accent stroke). Used by the
 *     DataAttribute catalog for attributes nothing appears to reference.
 *   nameField — which field the detail header edits inline.
 *   create — the "+ New" modal spec: { fields: [...] }, rendered by
 *     CreateArtifactModal. Each field: { name, label, kind ('text'|'textarea'|
 *     'select'), required?, placeholder?, help?, mono?, default?, options? |
 *     optionsFrom ('metrics'), slug?, slugFrom? }. `slugFrom` auto-derives this
 *     field from another as you type, until hand-edited. MetricStudio.createRecord
 *     merges these values over the artifact's structural defaults. No `create`
 *     block = no modal (fullManager artifacts).
 *   sections[] — ordered detail tabs: { key, label, component, description?,
 *     selfChrome? }. `component` is a section-body name registered in
 *     ArtifactDetail; `selfChrome:true` means the body draws its own card.
 *   fullManager:true + manager — artifact is NOT list→detail; render that manager
 *     component instead (quality, engagement).
 *
 * NOTES: Every SECTION_ICON / artifact `icon` value MUST be a FontAwesome icon
 *   registered in src/main.js library.add(...) or it renders blank. Vue 3 Options
 *   API + Vuex only. No backend contract change — points at existing endpoints.
 */

// Roles that carry the color language shared with the join builder.
// NOTE: every value here MUST be a FontAwesome icon registered in src/main.js
// (library.add(...)). Unregistered names render blank. Verified 2026-07-12.
// Every way a DataAttribute can be referenced. Opportunity primitives bind
// through api_metric.opportunity_config, NOT studio_config.steps — omitting that
// reported all 23 of them as unused.
const USAGE_KEYS = ['used_by_metrics', 'used_by_worklists', 'used_by_flags',
  'used_by_insights', 'used_by_opportunities'];

export const SECTION_ICON = {
  data_source: 'layer-group',
  metric_config: 'sliders',
  metric_report: 'table',
  aggregation: 'chart-bar',
  display: 'pen-to-square',
  domain: 'folder-tree',
  opportunity: 'tasks',
  presentation: 'tag',
  columns: 'table',
  visualization: 'chart-simple',
  linked_metric: 'sitemap',
  meta: 'file-lines',
  assignment: 'users',
  catalog: 'book',
  definition: 'file-lines',
  audit: 'clipboard-list',
  history: 'clock-rotate-left',
};

/**
 * A metric's authoring tabs. Shared by `metric` and `quality` — a quality measure
 * IS a metric (one carrying a measure_code), so it authors identically.
 */
const METRIC_SECTIONS = [
  { key: 'definition', label: 'Definition', component: 'MetricDefinitionSection', selfChrome: true,
    description: 'What this metric computes and what healthy results should look like — calculation rules and audit expectations.',
    help: 'A unified specification of calculation logic, eligible populations, exclusions, caveats, and benchmark expectations for auditing.' },
  { key: 'metric_config', label: 'Metric Config', component: 'MetricConfigSection', selfChrome: true,
    description: 'One view of the metric definition: source, identifiers, calculation, and display.',
    help: 'Configure the complete metric in one place. Data Source defines the DataAttribute graph and identifiers; Calculation defines how rows collapse; Display defines how the result is presented.' },
  { key: 'metric_report', label: 'Test Metric', component: 'MetricReportSection', selfChrome: true,
    description: 'Explore this metric through the generic entity and fact identifier report engine.',
    help: 'Choose additive entity-type and fact-identifier grouping chips, optionally scope by a page entity ID, and inspect the metric aggregates returned by the generic report engine.' },
  { key: 'domain', label: 'Performance Domain', component: 'DomainSection',
    description: 'Which domain(s) this metric belongs to in the /insight sidebar.',
    help: 'Domains group metrics in the /insight sidebar. Add this metric to one or more domains via the chips; changes save immediately. A metric with no domain still exists but won\'t appear grouped in the sidebar.' },
  { key: 'opportunity', label: 'Opportunity Sync', component: 'OpportunitySection', selfChrome: true,
    description: 'Turn this metric into worklist opportunities (create/expire/priority). Fully independent of the SQL.',
    help: 'Optional. When enabled, this metric generates worklist opportunities: choose the opportunity primitive, then the create/expire/update rules and (optionally) a flag-weighted or due-date priority score. Leave disabled if the metric is display-only.' },
  { key: 'audit', label: 'Audit', component: 'AuditSection', selfChrome: true,
    description: 'Traffic-light checks: does this metric\'s real data match its expectation?',
    help: 'Each stage pulls this metric\'s real data through the production read paths and judges it against the Expectation on the Methodology tab — the same checks the automated weekly audit runs. Green = matches, amber = worth a look, red = contradicts the contract. Run stages on demand after a recompute or a definition change.' },
];

/**
 * The nine artifact types. `group` drives the optional top-level grouping in the
 * left browser (Performance / Setup / Analysis). Order within a group is array
 * order.
 */
export const ARTIFACTS = {
  // ─────────────────────────────── PERFORMANCE ───────────────────────────────
  wizard: {
    key: 'wizard',
    label: 'Wizard',
    singular: 'Wizard',
    group: 'Performance',
    icon: 'wand-magic-sparkles',
    accent: '#7C3AED',
    help: 'A live conversation with the Metric Agent. It queries the warehouse as you talk, tests assumptions, and authors metrics, flags, worklists, insights and DataAttributes through the validated create bridge when you say so.',
    manager: 'WizardManager',
    fullManager: true,
  },

  metric: {
    key: 'metric',
    label: 'Metrics',
    singular: 'Metric',
    group: 'Performance',
    icon: 'chart-line',
    accent: '#2563EB',
    help: 'A metric turns claims/EHR data into a tracked performance number (e.g. AWV completion rate) shown on the /insight dashboards. Configure its DataAttribute graph, generic identifiers, calculation, and presentation together in Metric Config. Optionally assign it to a domain and have it generate worklist opportunities.',
    // REST
    listUrl: 'api/impact_analytics/composable-metrics/',
    detailUrl: (id) => `api/impact_analytics/composable-metrics/${id}/`,
    deleteUrl: (id) => `/api/metrics/${id}/`,
    deletePreviewUrl: (id) => `/api/metrics/${id}/delete-preview/`,
    idField: 'id',
    // Quality measures live in the Quality Metrics tab, not here.
    rowFilter: (m) => !m.measure_code,
    // list
    searchFields: ['name', 'metric_name_slug'],
    columns: [
      { key: 'id', label: '#', width: 'w-12', mono: true, muted: true },
      { key: 'name', label: 'Name', grow: true },
      { key: 'has_worklist', label: 'WL', check: true, width: 'w-12' },
      { key: 'has_opportunity_config', label: 'Opp', check: true, width: 'w-12' },
      // Contract + audit-chain status from audit-board/ (merged as row._audit).
      // Methodology and expectation are SEPARATE artifacts (shared generation
      // fingerprint). ≠ beside a verdict = metric SQL changed since that check.
      { key: 'methodology', label: 'Meth', contract: 'methodology', width: 'w-14' },
      { key: 'expectation', label: 'Expect', contract: 'expectation', width: 'w-14' },
      { key: 'aggregate_check', label: 'Agg', verdict: 'aggregate_check', width: 'w-10' },
      { key: 'entity_check', label: 'Ent', verdict: 'entity_check', width: 'w-10' },
      { key: 'worklist_check', label: 'Wkl', verdict: 'worklist_check', width: 'w-10' },
      { key: 'insight_check', label: 'Ins', verdict: 'insight_check', width: 'w-10' },
      { key: 'updated_at', label: 'Updated', date: true, width: 'w-24', muted: true },
    ],
    create: {
      fields: [
        { name: 'name', label: 'Metric name', kind: 'text', required: true,
          placeholder: 'e.g. % of Attributed Patients With An AWV+' },
        { name: 'description', label: 'Description', kind: 'textarea', required: true,
          placeholder: 'What this metric measures, and for whom.' },
      ],
    },
    // detail
    nameField: 'name',
    sections: METRIC_SECTIONS,
  },

  flag: {
    key: 'flag',
    label: 'Patient Flags',
    singular: 'Patient Flag',
    group: 'Performance',
    icon: 'flag',
    accent: '#DC2626',
    help: 'A patient flag is a per-patient badge (boolean or categorical) evaluated as-of-now — e.g. "Long Stay" or a LACE risk band. Set how the chip looks in Presentation, then define which patients qualify by joining DataAttributes in Data Source (must resolve BENE_MBI_ID). Flags surface on the patient drawer and can feed opportunity priority.',
    listUrl: 'api/impact_analytics/patient-flags/',
    detailUrl: (id) => `api/impact_analytics/patient-flags/${id}/`,
    idField: 'id',
    searchFields: ['label', 'key'],
    columns: [
      { key: 'id', label: '#', width: 'w-12', mono: true, muted: true },
      { key: 'label', label: 'Flag', grow: true, chip: 'color' },
      { key: 'key', label: 'Key', mono: true, width: 'w-40', muted: true },
      { key: 'priority', label: 'Priority', width: 'w-20' },
      { key: 'is_active', label: 'Active', dot: true, width: 'w-16' },
    ],
    create: {
      fields: [
        { name: 'label', label: 'Flag label', kind: 'text', required: true,
          placeholder: 'e.g. Long Stay' },
        { name: 'key', label: 'Key', kind: 'text', required: true, mono: true,
          slug: true, slugFrom: 'label', placeholder: 'long_stay',
          help: 'Stable identifier, unique per tenant.' },
      ],
    },
    nameField: 'label',
    sections: [
      { key: 'data_source', label: 'Data Source', component: 'FlagDataSourceSection', selfChrome: true,
        description: 'The join graph that resolves qualifying patients (BENE_MBI_ID required). Evaluated as-of-now.',
        help: 'Define which patients get the flag by joining DataAttributes. The query MUST emit BENE_MBI_ID; VALUE is optional for boolean flags, required for categorical. Evaluated as-of-now (no date spine needed) — typically a single intervention DA.' },
      { key: 'presentation', label: 'Presentation', component: 'FlagPresentationSection',
        description: 'Label, color, priority, categorical value map, and the hover value shown on the chip.',
        help: 'Set how the flag chip appears: label, color, and priority (lower sorts first). Choose Boolean (on/off) or Categorical (value→chip map with per-value weights). Optionally show the flag\'s underlying VALUE on hover. Refresh state recomputes who is flagged right now.' },
    ],
  },

  worklist: {
    key: 'worklist',
    label: 'Worklists',
    singular: 'Worklist',
    group: 'Performance',
    icon: 'table',
    accent: '#7C3AED',
    help: 'A worklist is a filtered patient list clinical staff work from. Link it to a metric in Details, define which patients appear (one row per patient) in Data Source, then choose the displayed columns in Columns.',
    listUrl: 'api/impact_analytics/patient-worklists/',
    detailUrl: (id) => `api/impact_analytics/patient-worklists/${id}/`,
    idField: 'id',
    searchFields: ['name', 'description'],
    columns: [
      { key: 'id', label: '#', width: 'w-12', mono: true, muted: true },
      { key: 'name', label: 'Name', grow: true },
      { key: 'metric_id', label: 'Metric', width: 'w-16', mono: true, muted: true },
      { key: 'column_count', label: 'Cols', width: 'w-16', compute: (wl) => (wl.column_config || []).length || '—' },
    ],
    create: {
      fields: [
        { name: 'name', label: 'Worklist name', kind: 'text', required: true,
          placeholder: 'e.g. Open ACD Opportunities' },
        { name: 'description', label: 'Description', kind: 'textarea',
          placeholder: 'Who works this list, and what it contains.' },
        { name: 'metric_id', label: 'Linked metric', kind: 'select',
          optionsFrom: 'metrics' },
      ],
    },
    nameField: 'name',
    sections: [
      { key: 'data_source', label: 'Data Source', component: 'WorklistDataSourceSection', selfChrome: true,
        description: 'The join graph whose grain is one row per listed patient.',
        help: 'Define which patients appear, one row per patient, by joining DataAttributes. This is the worklist\'s population — the columns shown are chosen separately in the Columns tab.' },
      { key: 'columns', label: 'Columns', component: 'WorklistColumnsSection',
        description: 'The displayed column projection (header, type, visibility, order).',
        help: 'Choose the columns clinical staff see and their order. Each column maps to a field the Data Source emits; set a header label, type, default visibility, and an optional fallback value for missing data.' },
      { key: 'linked_metric', label: 'Details', component: 'WorklistMetaSection',
        description: 'Description, linked metric, active flag.',
        help: 'Basic settings: a description, the metric this worklist is linked to (drives its default population), and whether it\'s active. Inactive worklists are hidden from clinical users.' },
    ],
  },

  insight: {
    key: 'insight',
    label: 'Metric Insights',
    singular: 'Metric Insight',
    group: 'Performance',
    icon: 'chart-simple',
    accent: '#0EA5E9',
    help: 'A metric insight is a drill-down chart or table shown under a metric on /insight. Set its parent metric and placement, define the fact data in Data Source, then map fields to a visualization (chart / table / sankey / heatmap / pie).',
    listUrl: 'api/metric-insights/',
    detailUrl: (id) => `api/metric-insights/${id}/`,
    idField: 'id',
    searchFields: ['name'],
    columns: [
      { key: 'id', label: '#', width: 'w-12', mono: true, muted: true },
      { key: 'name', label: 'Insight', grow: true },
      { key: 'insight_type', label: 'Type', badge: 'purple', width: 'w-28' },
      { key: 'viz_type', label: 'Viz', width: 'w-24', muted: true },
      { key: 'metric', label: 'Metric', width: 'w-16', mono: true, muted: true },
      { key: 'display_order', label: 'Order', width: 'w-16', muted: true },
      { key: 'is_active', label: 'Active', dot: true, width: 'w-16' },
    ],
    create: {
      fields: [
        { name: 'name', label: 'Insight name', kind: 'text', required: true,
          placeholder: 'e.g. Follow-Up Source (All Discharges)' },
        { name: 'metric', label: 'Parent metric', kind: 'select', required: true,
          optionsFrom: 'metrics' },
      ],
    },
    nameField: 'name',
    sections: [
      { key: 'data_source', label: 'Data Source', component: 'InsightDataSourceSection', selfChrome: true,
        description: 'The fact join graph the insight renders (often a single fact DA).',
        help: 'Define the fact data the chart renders by joining DataAttributes — often just a single fact DA. The columns emitted here become the fields you map in the Visualization tab.' },
      { key: 'visualization', label: 'Visualization', component: 'InsightVizSection', selfChrome: true,
        description: 'Chart type + field mapping (chart / table / sankey / heatmap / pie).',
        help: 'Pick the visualization (chart / table / sankey / heatmap / pie) and map the Data Source columns to it (x/label, series/values, etc.). Task-insight type has no chart — it\'s per-patient data consumed by a task form instead.' },
      { key: 'linked_metric', label: 'Placement', component: 'InsightPlacementSection',
        description: 'Parent metric, insight id, width, display order, active.',
        help: 'Set where this insight appears: its parent metric, the insight id, on-screen width (full/half), display order among sibling insights, and whether it\'s active.' },
    ],
  },

  // ───────────────────────────────── SETUP ───────────────────────────────────
  quality: {
    key: 'quality',
    label: 'Quality Metrics',
    singular: 'Quality Measure',
    group: 'Setup',
    icon: 'clipboard-list',
    accent: '#059669',
    help: 'A quality measure is a metric carrying a measure_code (HEDIS/STARS). Measures lists them and opens the same authoring tabs as any metric; Contract Assignments assigns them to contract cohorts; Contract Scoring sets how each contract scores (methodology, rollup, thresholds) plus per-measure weight, goal and tiers.',
    // Same REST resource as `metric`, split on measure_code.
    listUrl: 'api/impact_analytics/composable-metrics/',
    detailUrl: (id) => `api/impact_analytics/composable-metrics/${id}/`,
    deleteUrl: (id) => `/api/metrics/${id}/`,
    deletePreviewUrl: (id) => `/api/metrics/${id}/delete-preview/`,
    idField: 'id',
    rowFilter: (m) => !!m.measure_code,
    // Two managers beside the Measures list, one per level of the tracker spec:
    //   Contract Assignments — WHICH measures belong to which contract cohort.
    //   Contract Scoring     — HOW a contract scores: its scoring_config params
    //                          plus each measure's weight / goal / tiers.
    managerTabs: [
      { key: 'assignments', label: 'Contract Assignments', component: 'QualityManager' },
      { key: 'scoring', label: 'Contract Scoring', component: 'QualityScoringManager' },
    ],
    searchFields: ['name', 'measure_code', 'metric_name_slug'],
    columns: [
      { key: 'id', label: '#', width: 'w-12', mono: true, muted: true },
      { key: 'name', label: 'Name', grow: true },
      { key: 'measure_code', label: 'Measure', badge: 'amber', width: 'w-32' },
      { key: 'has_worklist', label: 'WL', check: true, width: 'w-12' },
      { key: 'updated_at', label: 'Updated', date: true, width: 'w-24', muted: true },
    ],
    nameField: 'name',
    create: {
      fields: [
        { name: 'name', label: 'Measure name', kind: 'text', required: true,
          placeholder: 'e.g. Diabetes: HbA1c Control' },
        { name: 'measure_code', label: 'Measure code', kind: 'text', required: true,
          mono: true, placeholder: 'CDC-A1C' },
        { name: 'description', label: 'Description', kind: 'textarea', required: true,
          placeholder: 'What this measure captures.' },
      ],
    },
    // A quality measure is a metric — same authoring tabs.
    sections: METRIC_SECTIONS,
  },

  engagement: {
    key: 'engagement',
    label: 'Entity Engagement',
    singular: 'Engagement Config',
    group: 'Setup',
    icon: 'users',
    accent: '#D97706',
    help: 'Entity Engagement configures engagement rules for entities. It has no DataAttributes — pure configuration.',
    // NO DataAttributes at all — pure config manager.
    manager: 'EngagementManager',
    fullManager: true,
  },

  // ─────────────────────────────── ANALYSIS ──────────────────────────────────
  attribute: {
    key: 'attribute',
    label: 'DataAttributes',
    singular: 'DataAttribute',
    group: 'Performance',
    icon: 'database',
    accent: '#0891B2',
    help: 'A DataAttribute is one named SQL query — the raw material every metric, worklist, flag and insight composes. This catalog lists every active DataAttribute for the tenant, what references it, and every past version of its SQL. Editing a shared DataAttribute changes every artifact that composes it, so check the usage counts first.',
    listUrl: 'api/impact_analytics/attributes/',
    detailUrl: (id) => `api/impact_analytics/attributes/${id}/`,
    idField: 'id',
    searchFields: ['name', 'description', 'role'],
    columns: [
      { key: 'id', label: '#', width: 'w-14', mono: true, muted: true },
      { key: 'role', label: 'Role', width: 'w-32', badge: 'gray' },
      { key: 'name', label: 'Name', grow: true },
      { key: 'used_by_metrics', label: 'Metrics', width: 'w-20', count: true },
      { key: 'used_by_worklists', label: 'Worklists', width: 'w-20', count: true },
      { key: 'used_by_flags', label: 'Flags', width: 'w-16', count: true },
      { key: 'used_by_insights', label: 'Insights', width: 'w-20', count: true },
      { key: 'used_by_opportunities', label: 'Opps', width: 'w-16', count: true },
      { key: 'updated_at', label: 'Updated', width: 'w-24', date: true, muted: true },
    ],
    // Grey out attributes nothing appears to reference. Counts come from
    // studio_config.steps across metrics/worklists/flags/insights, so this is
    // "seemingly unused" — a DA reached some other way still dims. Absent counts
    // (undefined, not 0) never dim.
    rowMuted: (row) => USAGE_KEYS.some((k) => row[k] !== undefined && row[k] !== null)
      && !USAGE_KEYS.some((k) => row[k]),
    nameField: 'name',
    sections: [
      { key: 'data_source', label: 'SQL', component: 'AttributeSqlSection', selfChrome: true,
        description: 'The SQL this DataAttribute runs, and everything that composes it.',
        help: 'Edit the SQL and save to re-validate it against Snowflake. Placeholders like {tenant_id} are substituted by the engine at run time. The Used by panel lists every artifact composing this attribute — all of them change when you edit it.' },
      { key: 'history', label: 'History', component: 'AttributeHistorySection', selfChrome: true,
        description: 'Every past version of this DataAttribute, newest first.',
        help: 'Each row is one save, with who made it and what changed. Expand a version to see its SQL as it stood then — the recovery path when an edit turns out to be wrong.' },
    ],
  },

};

export const ARTIFACT_ORDER = [
  'wizard', 'metric', 'flag', 'worklist', 'insight', 'attribute', 'quality', 'engagement',
];

export const GROUP_ORDER = ['Performance', 'Setup'];

export function artifactsByGroup() {
  const out = {};
  for (const g of GROUP_ORDER) out[g] = [];
  for (const key of ARTIFACT_ORDER) {
    const a = ARTIFACTS[key];
    (out[a.group] = out[a.group] || []).push(a);
  }
  return out;
}

export function getArtifact(key) {
  return ARTIFACTS[key] || null;
}

/**
 * Whether an artifact has a generic list-to-detail surface. Full-page managers
 * (such as Entity Engagement) deliberately have no record URL.
 */
export function isDetailArtifact(key) {
  const artifact = getArtifact(key);
  return Boolean(artifact && !artifact.fullManager && artifact.detailUrl);
}
